<template>
  	<div>
		<div class="p-text-left">
			<h2 class="p-mb-2 titleFont">Step 2: Parent Information</h2>
			<p>Please fill out your information below.</p>
		</div>

		<div class="p-fluid p-grid p-formgrid p-text-left p-jc-center p-mt-3">
			<div class="p-field p-mb-3 p-col-12 p-sm-6">
				<label for="firstName" class="p-text-bold">First Name *</label>
				<InputText
				id="firstName"
				v-model="firstName"
				placeholder="First Name"
				type="text"
				/>

				<small
					v-if="v$.firstName.$invalid && submitted"
					class="p-error p-text-bold"
				>
					{{ v$.firstName.required.$message.replace("Value", "First Name") }}
				</small>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-6">
				<label for="lastName" class="p-text-bold">Last Name *</label>

				<InputText
				id="lastName"
				v-model="lastName"
				placeholder="Last Name"
				type="text"
				/>

				<small
					v-if="v$.lastName.$invalid && submitted"
					class="p-error p-text-bold"
				>
					{{ v$.lastName.required.$message.replace("Value", "Last Name") }}
				</small>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-6">
				<label for="address" class="p-text-bold">Primary Address *</label>

				<AutoComplete
				id="address"
				v-model="address"
				field="name"
				placeholder="Address"
				:suggestions="filteredAddresses"
				@complete="searchAddress($event)"
				@item-select="populateFields($event)"
				/>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-6">
				<label for="address2" class="p-text-bold">Secondary Address</label>
				<InputText
					id="address2"
					v-model="address2"
					placeholder="Secondary Address"
					type="text"
				/>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="city" class="p-text-bold">City *</label>

				<InputText id="city" v-model="city" placeholder="City" type="text" />

				<small v-if="v$.city.$invalid && submitted" class="p-error p-text-bold">
					{{ v$.city.required.$message.replace("Value", "City") }}
				</small>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="province" class="p-text-bold">
					{{
						country == "Canada"
							? "Province *"
							: country.name == "United States"
							? "State *"
							: "Province / State *"
					}}
				</label>

				<div>
					<Dropdown
						id="province"
						v-model="selectedProvince"
						:options="filteredProvinceState"
						optionLabel="name"
					/>
				</div>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="postalCode_zip" class="p-text-bold">
					{{
						country == "Canada"
						? "Postal Code *"
						: country.name == "United States"
							? "ZIP Code *"
							: "Postal Code / ZIP Code *"
					}}
				</label>

				<InputMask
					id="postalCode_zip"
					v-model="postalCode_zip"
					:class="postalCode_zip ? 'p-text-uppercase' : ''"
					:placeholder="
						country == 'Canada'
							? 'Postal Code'
							: country.name == 'United States'
								? 'ZIP Code'
								: 'Postal Code / ZIP Code'
					"
					slotChar=""
					mask="a9a 9a9"
				/>

				<small
					v-if="v$.postalCode_zip.$invalid && submitted"
					class="p-error p-text-bold"
				>
					{{
						v$.postalCode_zip.required.$message.replace(
							"Value",
							country == "Canada"
								? "Postal Code"
								: country.name == "United States"
									? "ZIP Code"
									: "Postal Code / ZIP Code"
						)
					}}
				</small>
			</div>

			<!-- <div class="p-field p-mb-3 p-col-12 p-sm-6 p-jc-start">
				<label for="country" class="p-text-bold">Country *</label>

				<InputText v-model="country" placeholder="Country" disabled style="opacity: 1" />
	
				<small v-if="v$.country.$invalid && submitted" class="p-error p-text-bold">
				{{ v$.country.required.$message.replace("Value", "Country") }}
				</small>
			</div> -->

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="email" class="p-text-bold">Primary Email *</label>

				<InputText
					id="email"
					v-model="email"
					placeholder="Primary Email"
					inputmode="email"
					type="text"
				/>

				<span v-if="v$.email.$error && submitted">
					<span
						id="email-error"
						v-for="(error, index) of v$.email.$errors"
						:key="index"
					>
						<small class="p-error p-text-bold">
							{{
								error.$message == "Value is required"
								? error.$message.replace("Value", "Primary Email")
								: error.$message.replace("Value is not a valid", "Invalid")
							}}
						</small>
					</span>
				</span>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="secondaryEmail" class="p-text-bold">Secondary Email</label>

				<InputText
					id="secondaryEmail"
					v-model="secondaryEmail"
					placeholder="Secondary Email"
					inputmode="secondaryEmail"
					type="text"
				/>

				<span v-if="v$.secondaryEmail.$error && submitted">
					<span
						id="secondaryEmail-error"
						v-for="(error, index) of v$.secondaryEmail.$errors"
						:key="index"
					>
						<small class="p-error p-text-bold">
							{{
								error.$message == "Value is required"
								? error.$message.replace("Value", "Secondary Email")
								: error.$message.replace("Value is not a valid", "Invalid")
							}}
						</small>
					</span>
				</span>
			</div>

			<div class="p-field p-mb-3 p-col-12 p-sm-4">
				<label for="phoneNumber" class="p-text-bold">Phone Number</label>

				<InputText
					id="phoneNumber"
					v-model="phoneNumber"
					placeholder="Phone Number"
					v-mask="'(###) ###-####'"
					inputmode="tel"
				/>

				<span v-if="v$.phoneNumber.$error && submitted">
					<span
						id="phoneNumber-error"
						v-for="(error, index) of v$.phoneNumber.$errors"
						:key="index"
					>
						<small class="p-error p-text-bold">
							{{
								error.$message == "Value is required"
								? error.$message.replace("Value", "Phone Number")
								: error.$message.replace(
									"This field should be at least 14 characters long",
									"Invalid phone number"
								)
							}}
						</small>
					</span>
				</span>
			</div>
		</div>
		<div class="p-d-flex p-jc-between p-mt-3 p-no-gutters">
			<div class="p-mb-3 p-col-6 p-pl-0">
				<Button
					label="Back"
					icon="pi pi-angle-left"
					class="p-button-secondary"
					style="min-width: 120px; max-width: 120px"
					@click="prevStep"
				/>
			</div>

			<div class="p-mb-3 p-col-6 p-pr-0">
				<Button
					label="Next"
					icon="pi pi-angle-right"
					iconPos="right"
					style="min-width: 120px; max-width: 120px"
					@click="nextStep"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { useFormDataStore } from "../../store/formData.js";
import { useVuelidate } from "@vuelidate/core";
import { required, email, minLength } from "@vuelidate/validators";
import gmapsInit from "@/utils/gmaps.js";


export default {
  name: "S2Form",
  data() {
    return {
      firstName: null,
      lastName: null,
      address: null,
      address2: null,
      country: "Canada",
      postalCode_zip: null,
      city: null,
      selectedProvince: { name: "Saskatchewan", code: "SK" },
      phoneNumber: null,
      email: null,
	  secondaryEmail: null,
      filteredCountries: null,
      filteredProvinceState: null,
      submitted: false,
      autoCompleteService: null,
      geocoder: null,
      filteredAddresses: [],
      streetNumber: null,
      route: null,
    };
  },
  computed: {
    canadaProvinceOptions() {
      return this.store.provinceOptions;
    },
  },
  setup() {
    const store = useFormDataStore();

    return {
      store,
      v$: useVuelidate(),
    };
  },
  
  validations() {
    return {
      firstName: { required },
      lastName: { required },
      address: { required },
      country: { required },
      postalCode_zip: { required },
      city: { required },
      selectedProvince: { required },
      phoneNumber: { minLength: minLength(14) },
      email: {
        required,
        email,
      },
	  secondaryEmail: {
		email,
	  },
    };
  },
  watch: {
    selectedProvince(input) {
      let options = [];

			this.store.provinceOptions.forEach((option) => {
				options.push(option.name);
			});

      if (typeof input == "string" && !options.includes(input)) {
        this.selectedProvince = null;
      } else if (typeof input == "string" && options.includes(input)) {
        this.selectedProvince =
          this.store.provinceOptions[options.indexOf(input)];
      }
    },
  },
  async mounted() {
    let google = await gmapsInit();
    this.filteredProvinceState = [...this.store.provinceOptions];

    if (google) {
      this.autoCompleteService =
        new window.google.maps.places.AutocompleteService();
      this.geocoder = new window.google.maps.Geocoder();
    } else {
      console.error("Google Maps API error");
    }
  },  
  methods: {
    searchProvinceState() {
      this.filteredProvinceState = [];
    },
    async nextStep() {
      const isFormValid = await this.v$.$validate();
      this.submitted = true;

			// console.log({
			//   donationType: this.donationTypeRB,
			//   companyName: this.companyName,
			//   anonymousDonation: this.anonymousCB,
			//   firstName: this.firstName,
			//   lastName: this.lastName,
			//   address: this.address,
			//   address2: this.address2,
			//   country: this.country,
			//   postalCode_zip: this.postalCode_zip,
			//   city: this.city,
			//   region: this.selectedProvince.code,
			//   phoneNumber: this.phoneNumber,
			//   email: this.email,
			//   comments: this.comments,
			//   subscribe: this.subscribeCB,
			// });

      if (isFormValid) {
        this.store.setS2FormData({
          firstName: this.firstName,
          lastName: this.lastName,
          address: this.address,
          address2: this.address2,
          country: this.country,
          postalCode: this.postalCode_zip,
          city: this.city,
          region: this.selectedProvince.code,
          phoneNumber: this.phoneNumber,
          email: this.email,
		  secondaryEmail: this.secondaryEmail,
        });

				this.$emit("nextStep");
			} else {
				this.scrollToFirstError();

				return;
			}
		},
		prevStep() {
			this.$emit("prevStep");
		},
		scrollToFirstError() {
			this.$nextTick(() => {
				if (document.querySelector(".p-error p-text-bold")) {
					let domRect = document
						.querySelector(".p-error p-text-bold")
						.getBoundingClientRect();

          window.scrollTo({
            left: domRect.left + document.documentElement.scrollLeft,
            top: domRect.top + document.documentElement.scrollTop - 100,
            behavior: "smooth",
          });
        }
      });
    },
    searchAddress(event) {
      let countrySearchCriteria = ["ca"];

      this.autoCompleteService.getPlacePredictions(
        {
          input: event.query,
          componentRestrictions: { country: countrySearchCriteria },
          types: ["address"],
        },
        this.addressSearchResults
      );
    },
    addressSearchResults(predictions, status) {
      // console.log(predictions);
      // console.log(status);

      if (status == "OK") {
        this.filteredAddresses = [];

        predictions.forEach((place) => {
          this.filteredAddresses.push({
            name: place.description,
            placeId: place.place_id,
            mainText: place.structured_formatting.main_text,
          });
        });
      }
    },
    populateFields(event) {
      this.model = event.value.mainText;

      this.geocoder
        .geocode({
          placeId: event.value.placeId,
        })
        .then((result) => {
          // console.log(result.results[0]);
          const geocodePayload = result.results[0];
          geocodePayload.address_components.forEach((component) => {
            if (component.types.includes("street_number")) {
              this.streetNumber = component.long_name;
            } else if (component.types.includes("route")) {
              this.route = component.long_name;
              this.address = `${this.streetNumber} ${this.route}`
            } else if (component.types.includes("locality")) {
              this.city = component.long_name;
            } else if (component.types.includes("postal_code")) {
              this.postalCode_zip = component.long_name;
            } else if (component.types.includes("administrative_area_level_1")) {
              this.selectedProvince =
                this.store.provinceOptions[
                this.store.provinces.indexOf(component.long_name)]
            }
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.titleFont {
	font-size: 1.9rem;
}
</style>
